import {
  ChartPieIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import Badge from '../../../common/Badge/Badge.module';

export interface ResultRow {
  id?: string;
  immatriculation?: string;
  nom?: string;
  lot?: string;
  gestionnaire?: string;
  type?: string;
}

export interface ErrorDetail {
  list: string[];
  row: ResultRow;
}

export interface ImportData {
  total: number;
  ignored: Record<string, { row: ResultRow }>;
  inserted: Record<string, { row: ResultRow }>;
  updated: Record<string, { row: ResultRow }>;
  errors: Record<string, ErrorDetail>;
}

type TabType = 'total' | 'errors' | 'ignored' | 'inserted' | 'updated';

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: number;
  isActive: boolean;
  onClick: () => void;
  isClickable?: boolean;
}

function StatCard({
  icon, title, value, isActive, onClick, isClickable = true,
}: StatCardProps) {
  return (
    <div
      className={`flex items-center space-x-4 rounded-lg bg-gray-50 p-4 shadow ${
        isClickable ? 'cursor-pointer hover:bg-gray-100' : ''
      } ${isActive ? 'ring-2 ring-rgpd-blue' : ''}`}
      onClick={isClickable ? onClick : undefined}
    >
      {icon}
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-semibold">{value}</p>
      </div>
    </div>
  );
}

interface ImportResultBodyProps {
  data: ImportData;
}

const ImportResultBody: React.FC<ImportResultBodyProps> = ({ data }) => {
  // Default tab is the first non-empty bucket, otherwise 'total'
  const defaultTab: TabType = Object.keys(data.errors).length > 0
    ? 'errors'
    : Object.keys(data.ignored).length > 0
      ? 'ignored'
      : Object.keys(data.inserted).length > 0
        ? 'inserted'
        : Object.keys(data.updated).length > 0
          ? 'updated'
          : 'total';
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab);

  const renderGenericTable = (
    items: Record<string, { row: ResultRow }>,
    columns: { key: keyof ResultRow; label: string }[],
    extraContent?: (item: { row: ResultRow } | ErrorDetail) => React.ReactNode,
  ) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th key={column.key} className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                {column.label}
              </th>
            ))}
            {extraContent && (
              <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                Détails
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {Object.entries(items).map(([key, item]) => (
            <tr key={key}>
              {columns.map((column) => (
                <td key={column.key} className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                  {item.row[column.key]}
                </td>
              ))}
              {extraContent && (
                <td className="px-6 py-4 text-sm text-gray-500">
                  {extraContent(item)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderErrors = () => renderGenericTable(
    data.errors,
    [
      { key: 'id', label: 'ID' },
      { key: 'nom', label: 'Nom' },
    ],
    (error) => {
      const list = 'list' in error ? error.list : [];
      return (
        <ul className="list-disc pl-5">
          {list.map((err, index) => (
            <li key={index} className="text-sm text-red-600">{err}</li>
          ))}
        </ul>
      );
    },
  );

  const renderRowsTable = (items: Record<string, { row: ResultRow }>) => renderGenericTable(
    items,
    [
      { key: 'id', label: 'ID' },
      { key: 'immatriculation', label: 'Immatriculation' },
      { key: 'nom', label: 'Nom' },
    ],
  );

  const renderTabContent = () => {
    const noResultsMessage = (
      <Badge
        mode="warning"
        text="Aucun résultat pour cette catégorie"
        className="w-full justify-center"
      />
    );

    switch (activeTab) {
    case 'errors':
      return Object.keys(data.errors).length > 0 ? renderErrors() : noResultsMessage;
    case 'ignored':
      return Object.keys(data.ignored).length > 0 ? renderRowsTable(data.ignored) : noResultsMessage;
    case 'inserted':
      return Object.keys(data.inserted).length > 0 ? renderRowsTable(data.inserted) : noResultsMessage;
    case 'updated':
      return Object.keys(data.updated).length > 0 ? renderRowsTable(data.updated) : noResultsMessage;
    default:
      return null;
    }
  };

  return (
    <>
      <div className="mb-6 grid grid-cols-3 gap-3 xl:grid-cols-5">
        <StatCard
          icon={<ChartPieIcon className="h-6 w-6 text-blue-500" />}
          title="Total"
          value={data.total}
          isActive={activeTab === 'total'}
          onClick={() => setActiveTab('total')}
          isClickable={false}
        />
        <StatCard
          icon={<XCircleIcon className="h-6 w-6 text-red-500" />}
          title="Erreurs"
          value={Object.keys(data.errors).length}
          isActive={activeTab === 'errors'}
          onClick={() => setActiveTab('errors')}
        />
        <StatCard
          icon={<MinusCircleIcon className="h-6 w-6 text-gray-500" />}
          title="Ignorés"
          value={Object.keys(data.ignored).length}
          isActive={activeTab === 'ignored'}
          onClick={() => setActiveTab('ignored')}
        />
        <StatCard
          icon={<CheckCircleIcon className="h-6 w-6 text-green-500" />}
          title="Insertions"
          value={Object.keys(data.inserted).length}
          isActive={activeTab === 'inserted'}
          onClick={() => setActiveTab('inserted')}
        />
        <StatCard
          icon={<ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />}
          title="Modifications"
          value={Object.keys(data.updated).length}
          isActive={activeTab === 'updated'}
          onClick={() => setActiveTab('updated')}
        />
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        {renderTabContent()}
      </div>
    </>
  );
};

export default ImportResultBody;
